import { useState, useCallback } from 'react';
import {
  Upload,
  Database,
  Shield,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  FileSpreadsheet,
  X,
  Loader2,
  Info,
} from 'lucide-react';
import Papa from 'papaparse';

// === Types ===

type Step = 'mode' | 'upload' | 'mapping' | 'review' | 'results';
type OperationMode = 'good' | 'bad_direct' | 'bad_diff' | 'both';
type BadSubMode = 'direct' | 'diff';

interface ColumnMapping {
  [colIndex: string]: string;
}

interface ImportResult {
  processed: number;
  imported: number;
  invalidPhones: number;
  duplicatesInFile: number;
  fromDiff?: boolean;
  diffBadFound?: number;
}

interface ImportResults {
  goodCache?: ImportResult;
  badCache?: ImportResult;
}

// === Constants ===

const CLASSIFICATION_OPTIONS = [
  { value: 'landline', label: 'Landline' },
  { value: 'tcpa-risk', label: 'TCPA Risk' },
  { value: 'invalid', label: 'Invalid' },
];

const SUB_TYPE_OPTIONS: Record<string, { value: string; label: string }[]> = {
  'landline': [{ value: 'landline', label: 'Landline' }],
  'tcpa-risk': [
    { value: 'tcpa', label: 'TCPA' },
    { value: 'troll', label: 'Troll' },
  ],
  'invalid': [{ value: 'invalid', label: 'Invalid' }],
};

const MAPPABLE_FIELDS = [
  { value: 'phone', label: 'Phone Number', required: true },
  { value: 'line_type', label: 'Line Type' },
  { value: 'carrier_name', label: 'Carrier Name' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'country', label: 'Country' },
  { value: 'dnc_type', label: 'DNC Type' },
  { value: '', label: 'Skip' },
];

const AUTO_DETECT: Record<string, string> = {
  phone: 'phone', phone_number: 'phone', phonenumber: 'phone',
  'phone number': 'phone', mobile: 'phone', cell: 'phone',
  telephone: 'phone', tel: 'phone', number: 'phone',
  carrier: 'carrier_name', carrier_name: 'carrier_name', carriername: 'carrier_name',
  line_type: 'line_type', linetype: 'line_type',
  city: 'city',
  state: 'state', st: 'state', state_from_api: 'state',
  country: 'country',
  dnc_type: 'dnc_type', dnctype: 'dnc_type', dnc: 'dnc_type',
};

function autoDetectMapping(headers: string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const usedFields = new Set<string>();

  headers.forEach((header, index) => {
    const normalized = header.trim().toLowerCase().replace(/[^a-z0-9_ ]/g, '');
    const match = AUTO_DETECT[normalized];
    if (match && !usedFields.has(match)) {
      mapping[String(index)] = match;
      usedFields.add(match);
    }
  });

  // If single column CSV, auto-map to phone
  if (headers.length === 1 && !mapping['0']) {
    mapping['0'] = 'phone';
  }

  return mapping;
}

function hasPhoneMapped(mapping: ColumnMapping): boolean {
  return Object.values(mapping).includes('phone');
}

// === Component ===

export default function CacheManagement() {
  // Wizard state
  const [step, setStep] = useState<Step>('mode');
  const [mode, setMode] = useState<OperationMode | null>(null);
  const [badSubMode, setBadSubMode] = useState<BadSubMode>('direct');

  // File state
  const [cleanFile, setCleanFile] = useState<File | null>(null);
  const [badFile, setBadFile] = useState<File | null>(null);
  const [fullFile, setFullFile] = useState<File | null>(null);

  // CSV preview state
  const [cleanHeaders, setCleanHeaders] = useState<string[]>([]);
  const [cleanSample, setCleanSample] = useState<string[]>([]);
  const [cleanRowCount, setCleanRowCount] = useState(0);
  const [badHeaders, setBadHeaders] = useState<string[]>([]);
  const [badSample, setBadSample] = useState<string[]>([]);
  const [badRowCount, setBadRowCount] = useState(0);
  const [fullHeaders, setFullHeaders] = useState<string[]>([]);
  const [fullSample, setFullSample] = useState<string[]>([]);
  const [fullRowCount, setFullRowCount] = useState(0);

  // Mapping state
  const [cleanMapping, setCleanMapping] = useState<ColumnMapping>({});
  const [badMapping, setBadMapping] = useState<ColumnMapping>({});
  const [fullMapping, setFullMapping] = useState<ColumnMapping>({});

  // Classification state (for bad cache)
  const [classification, setClassification] = useState('landline');
  const [subType, setSubType] = useState('landline');

  // Results state
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState<ImportResults | null>(null);
  const [error, setError] = useState<string | null>(null);

  // === File handling ===

  const parseFilePreview = useCallback(async (
    file: File,
    setHeaders: (h: string[]) => void,
    setSample: (s: string[]) => void,
    setRowCount: (n: number) => void,
    setMapping: (m: ColumnMapping) => void
  ) => {
    const text = await file.text();
    const result = Papa.parse<string[]>(text, { header: false, skipEmptyLines: true });
    const headers = result.data[0] || [];
    const sample = result.data[1] || [];
    const rowCount = result.data.length - 1; // Exclude header
    setHeaders(headers);
    setSample(sample);
    setRowCount(rowCount);
    setMapping(autoDetectMapping(headers));
  }, []);

  const handleFileDrop = useCallback((
    e: React.DragEvent<HTMLDivElement>,
    setter: (f: File | null) => void,
    setHeaders: (h: string[]) => void,
    setSample: (s: string[]) => void,
    setRowCount: (n: number) => void,
    setMapping: (m: ColumnMapping) => void
  ) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.csv') || file.type === 'text/csv')) {
      setter(file);
      parseFilePreview(file, setHeaders, setSample, setRowCount, setMapping);
    }
  }, [parseFilePreview]);

  const handleFileSelect = useCallback((
    e: React.ChangeEvent<HTMLInputElement>,
    setter: (f: File | null) => void,
    setHeaders: (h: string[]) => void,
    setSample: (s: string[]) => void,
    setRowCount: (n: number) => void,
    setMapping: (m: ColumnMapping) => void
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      setter(file);
      parseFilePreview(file, setHeaders, setSample, setRowCount, setMapping);
    }
  }, [parseFilePreview]);

  // === Effective mode for bad cache ===
  const needsFullFile = mode === 'bad_diff' || (mode === 'both' && badSubMode === 'diff');
  const needsBadFile = mode === 'bad_direct' || (mode === 'both' && badSubMode === 'direct');
  const needsCleanFile = mode === 'good' || mode === 'bad_diff' || mode === 'both';
  const needsClassification = mode !== 'good';

  // === Navigation validation ===
  const canProceedFromUpload = (): boolean => {
    if (needsCleanFile && !cleanFile) return false;
    if (needsBadFile && !badFile) return false;
    if (needsFullFile && !fullFile) return false;
    return true;
  };

  const canProceedFromMapping = (): boolean => {
    if (needsCleanFile && !hasPhoneMapped(cleanMapping)) return false;
    if (needsBadFile && !hasPhoneMapped(badMapping)) return false;
    if (needsFullFile && !hasPhoneMapped(fullMapping)) return false;
    return true;
  };

  // === Submit ===
  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();

      // Determine the effective mode to send
      let serverMode: string;
      if (mode === 'good') serverMode = 'good';
      else if (mode === 'bad_direct') serverMode = 'bad_direct';
      else if (mode === 'bad_diff') serverMode = 'bad_diff';
      else if (mode === 'both' && badSubMode === 'diff') serverMode = 'both';
      else serverMode = 'both';

      // For "both" with direct bad file, we send two separate operations
      // But the backend handles "both" as good + diff. For direct bad in "both" mode,
      // we need to adjust: send 'good' first then 'bad_direct' — or handle in backend.
      // Simplification: "both" mode always uses diff. If user has direct bad file in "both",
      // we treat it as separate operations. However, the backend's "both" mode only handles diff.
      // Let's handle "both + direct" by sending two requests.

      if (mode === 'both' && badSubMode === 'direct') {
        // Two separate requests for "both" with direct bad file
        // First: good cache
        const goodFormData = new FormData();
        goodFormData.append('mode', 'good');
        goodFormData.append('cleanFile', cleanFile!);
        goodFormData.append('cleanMapping', JSON.stringify(cleanMapping));

        const goodResp = await fetch(`${import.meta.env.VITE_API_URL}/admin/cache/import`, {
          method: 'POST',
          body: goodFormData,
          credentials: 'include',
        });
        const goodData = await goodResp.json() as any;
        if (!goodData.success) throw new Error(goodData.error || 'Good cache import failed');

        // Second: bad cache
        const badFormData = new FormData();
        badFormData.append('mode', 'bad_direct');
        badFormData.append('badFile', badFile!);
        badFormData.append('badMapping', JSON.stringify(badMapping));
        badFormData.append('classification', classification);
        badFormData.append('subType', subType);

        const badResp = await fetch(`${import.meta.env.VITE_API_URL}/admin/cache/import`, {
          method: 'POST',
          body: badFormData,
          credentials: 'include',
        });
        const badData = await badResp.json() as any;
        if (!badData.success) throw new Error(badData.error || 'Bad cache import failed');

        setResults({
          goodCache: goodData.results.goodCache,
          badCache: badData.results.badCache,
        });
      } else {
        // Single request for all other modes
        formData.append('mode', serverMode);

        if (cleanFile) {
          formData.append('cleanFile', cleanFile);
          formData.append('cleanMapping', JSON.stringify(cleanMapping));
        }
        if (badFile && mode === 'bad_direct') {
          formData.append('badFile', badFile);
          formData.append('badMapping', JSON.stringify(badMapping));
        }
        if (fullFile) {
          formData.append('fullFile', fullFile);
          formData.append('fullMapping', JSON.stringify(fullMapping));
        }
        if (needsClassification) {
          formData.append('classification', classification);
          formData.append('subType', subType);
        }

        const resp = await fetch(`${import.meta.env.VITE_API_URL}/admin/cache/import`, {
          method: 'POST',
          body: formData,
          credentials: 'include',
        });
        const data = await resp.json() as any;
        if (!data.success) throw new Error(data.error || 'Import failed');

        setResults(data.results);
      }

      setStep('results');
    } catch (err: any) {
      setError(err.message || 'Import failed');
    } finally {
      setSubmitting(false);
    }
  };

  // === Reset ===
  const resetWizard = () => {
    setStep('mode');
    setMode(null);
    setBadSubMode('direct');
    setCleanFile(null);
    setBadFile(null);
    setFullFile(null);
    setCleanHeaders([]);
    setCleanSample([]);
    setCleanRowCount(0);
    setBadHeaders([]);
    setBadSample([]);
    setBadRowCount(0);
    setFullHeaders([]);
    setFullSample([]);
    setFullRowCount(0);
    setCleanMapping({});
    setBadMapping({});
    setFullMapping({});
    setClassification('landline');
    setSubType('landline');
    setResults(null);
    setError(null);
  };

  // === Render helpers ===

  const FileDropZone = ({ label, file, onDrop, onSelect, onClear, rowCount }: {
    label: string;
    file: File | null;
    onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
    onSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onClear: () => void;
    rowCount: number;
  }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {file ? (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
          <FileSpreadsheet className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-green-800 truncate">{file.name}</p>
            <p className="text-xs text-green-600">{rowCount.toLocaleString()} data rows</p>
          </div>
          <button onClick={onClear} className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors cursor-pointer"
        >
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-600 mb-1">Drag and drop CSV file here</p>
          <p className="text-xs text-gray-400 mb-3">or</p>
          <label className="inline-block px-4 py-2 bg-blue-600 text-white text-sm rounded-lg cursor-pointer hover:bg-blue-700">
            Browse Files
            <input type="file" accept=".csv" className="hidden" onChange={onSelect} />
          </label>
        </div>
      )}
    </div>
  );

  const MappingTable = ({ headers, sample, mapping, setMapping: updateMapping, label }: {
    headers: string[];
    sample: string[];
    mapping: ColumnMapping;
    setMapping: (m: ColumnMapping) => void;
    label: string;
  }) => {
    const phoneMapped = hasPhoneMapped(mapping);
    return (
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700">{label}</h3>
        {!phoneMapped && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            Phone Number column must be mapped to proceed
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-600">CSV Column</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Sample Data</th>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Map To</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {headers.map((header, i) => (
                <tr key={i} className={mapping[String(i)] === 'phone' ? 'bg-blue-50' : ''}>
                  <td className="px-3 py-2 font-mono text-gray-800">{header}</td>
                  <td className="px-3 py-2 text-gray-500 truncate max-w-[200px]">{sample[i] || '—'}</td>
                  <td className="px-3 py-2">
                    <select
                      value={mapping[String(i)] || ''}
                      onChange={(e) => {
                        const newMapping = { ...mapping };
                        if (e.target.value) {
                          // Remove any other column mapped to this field (except 'skip')
                          for (const key of Object.keys(newMapping)) {
                            if (newMapping[key] === e.target.value) {
                              delete newMapping[key];
                            }
                          }
                          newMapping[String(i)] = e.target.value;
                        } else {
                          delete newMapping[String(i)];
                        }
                        updateMapping(newMapping);
                      }}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Skip</option>
                      {MAPPABLE_FIELDS.filter(f => f.value).map(f => (
                        <option key={f.value} value={f.value}>
                          {f.label}{f.required ? ' *' : ''}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // === Step renders ===

  const renderModeStep = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Select Import Mode</h2>
        <p className="text-sm text-gray-500 mt-1">Choose which cache(s) to update and how to provide the data.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          {
            id: 'good' as OperationMode,
            icon: CheckCircle,
            iconColor: 'text-green-600',
            title: 'Update Good Cache',
            desc: 'Upload a CSV of clean phone numbers to add to the good phone cache.',
            files: '1 clean CSV file',
          },
          {
            id: 'bad_direct' as OperationMode,
            icon: Shield,
            iconColor: 'text-red-600',
            title: 'Update Bad Cache (Direct)',
            desc: 'Upload a CSV containing known bad phone numbers directly.',
            files: '1 bad numbers CSV file',
          },
          {
            id: 'bad_diff' as OperationMode,
            icon: Database,
            iconColor: 'text-orange-600',
            title: 'Update Bad Cache (Diff)',
            desc: 'Upload the original full list and the cleaned list. The system will find numbers excluded from the clean list.',
            files: '1 full list CSV + 1 clean CSV',
          },
          {
            id: 'both' as OperationMode,
            icon: Upload,
            iconColor: 'text-blue-600',
            title: 'Update Both Caches',
            desc: 'Import clean numbers to the good cache and bad numbers to the bad cache in one operation.',
            files: 'Clean CSV + bad CSV or full list',
          },
        ].map(({ id, icon: Icon, iconColor, title, desc, files }) => (
          <button
            key={id}
            onClick={() => { setMode(id); setStep('upload'); }}
            className={`text-left p-5 rounded-xl border-2 transition-all hover:shadow-md ${
              mode === id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <Icon className={`w-8 h-8 ${iconColor} mb-3`} />
            <h3 className="font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500 mt-1">{desc}</p>
            <p className="text-xs text-gray-400 mt-2">Requires: {files}</p>
          </button>
        ))}
      </div>
    </div>
  );

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Upload Files</h2>
        <p className="text-sm text-gray-500 mt-1">Upload the required CSV file(s) for your selected import mode.</p>
      </div>

      {/* Both mode: choose sub-mode for bad cache */}
      {mode === 'both' && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">Bad numbers source</label>
          <div className="flex gap-3">
            <button
              onClick={() => { setBadSubMode('direct'); setBadFile(null); setFullFile(null); }}
              className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                badSubMode === 'direct' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
              }`}
            >
              I have a bad numbers CSV
            </button>
            <button
              onClick={() => { setBadSubMode('diff'); setBadFile(null); setFullFile(null); }}
              className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                badSubMode === 'diff' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
              }`}
            >
              I need to diff full vs clean list
            </button>
          </div>
        </div>
      )}

      {/* Clean file upload */}
      {needsCleanFile && (
        <FileDropZone
          label={mode === 'bad_diff' ? 'Clean List CSV (numbers that passed scrubbing)' : 'Clean Numbers CSV'}
          file={cleanFile}
          rowCount={cleanRowCount}
          onDrop={(e) => handleFileDrop(e, setCleanFile, setCleanHeaders, setCleanSample, setCleanRowCount, setCleanMapping)}
          onSelect={(e) => handleFileSelect(e, setCleanFile, setCleanHeaders, setCleanSample, setCleanRowCount, setCleanMapping)}
          onClear={() => { setCleanFile(null); setCleanHeaders([]); setCleanSample([]); setCleanRowCount(0); setCleanMapping({}); }}
        />
      )}

      {/* Bad file upload (direct mode) */}
      {needsBadFile && (
        <FileDropZone
          label="Bad Numbers CSV"
          file={badFile}
          rowCount={badRowCount}
          onDrop={(e) => handleFileDrop(e, setBadFile, setBadHeaders, setBadSample, setBadRowCount, setBadMapping)}
          onSelect={(e) => handleFileSelect(e, setBadFile, setBadHeaders, setBadSample, setBadRowCount, setBadMapping)}
          onClear={() => { setBadFile(null); setBadHeaders([]); setBadSample([]); setBadRowCount(0); setBadMapping({}); }}
        />
      )}

      {/* Full file upload (diff mode) */}
      {needsFullFile && (
        <FileDropZone
          label="Full Original List CSV (before scrubbing)"
          file={fullFile}
          rowCount={fullRowCount}
          onDrop={(e) => handleFileDrop(e, setFullFile, setFullHeaders, setFullSample, setFullRowCount, setFullMapping)}
          onSelect={(e) => handleFileSelect(e, setFullFile, setFullHeaders, setFullSample, setFullRowCount, setFullMapping)}
          onClear={() => { setFullFile(null); setFullHeaders([]); setFullSample([]); setFullRowCount(0); setFullMapping({}); }}
        />
      )}

      {/* Classification dropdown (for bad cache modes) */}
      {needsClassification && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Classification</label>
            <select
              value={classification}
              onChange={(e) => {
                setClassification(e.target.value);
                setSubType(SUB_TYPE_OPTIONS[e.target.value][0].value);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {CLASSIFICATION_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Sub Type</label>
            <select
              value={subType}
              onChange={(e) => setSubType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {(SUB_TYPE_OPTIONS[classification] || []).map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Diff mode warning */}
      {needsFullFile && cleanFile && fullFile && cleanRowCount > fullRowCount && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>The clean list ({cleanRowCount.toLocaleString()} rows) is larger than the full list ({fullRowCount.toLocaleString()} rows). This is unusual — please verify the correct files were selected.</span>
        </div>
      )}
    </div>
  );

  const renderMappingStep = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Column Mapping</h2>
        <p className="text-sm text-gray-500 mt-1">Map CSV columns to cache fields. Phone Number is required. Other fields are optional.</p>
      </div>

      {needsCleanFile && cleanHeaders.length > 0 && (
        <MappingTable
          headers={cleanHeaders}
          sample={cleanSample}
          mapping={cleanMapping}
          setMapping={setCleanMapping}
          label={needsFullFile ? 'Clean List Columns' : 'Clean Numbers Columns'}
        />
      )}

      {needsBadFile && badHeaders.length > 0 && (
        <MappingTable
          headers={badHeaders}
          sample={badSample}
          mapping={badMapping}
          setMapping={setBadMapping}
          label="Bad Numbers Columns"
        />
      )}

      {needsFullFile && fullHeaders.length > 0 && (
        <MappingTable
          headers={fullHeaders}
          sample={fullSample}
          mapping={fullMapping}
          setMapping={setFullMapping}
          label="Full List Columns"
        />
      )}
    </div>
  );

  const renderReviewStep = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Review Import</h2>
        <p className="text-sm text-gray-500 mt-1">Verify the details below before importing.</p>
      </div>

      <div className="bg-gray-50 rounded-xl p-5 space-y-4">
        {/* Operation summary */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Operation</h3>
          <p className="text-sm text-gray-600">
            {mode === 'good' && 'Import clean numbers to Good Phone Cache'}
            {mode === 'bad_direct' && 'Import bad numbers to Global Phone Cache (direct)'}
            {mode === 'bad_diff' && 'Import bad numbers to Global Phone Cache (via diff)'}
            {mode === 'both' && badSubMode === 'direct' && 'Import to both caches (clean CSV + direct bad CSV)'}
            {mode === 'both' && badSubMode === 'diff' && 'Import to both caches (clean CSV + bad via diff)'}
          </p>
        </div>

        {/* Files */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Files</h3>
          <div className="space-y-1 text-sm text-gray-600">
            {cleanFile && <p>Clean CSV: <span className="font-medium">{cleanFile.name}</span> ({cleanRowCount.toLocaleString()} rows)</p>}
            {badFile && <p>Bad CSV: <span className="font-medium">{badFile.name}</span> ({badRowCount.toLocaleString()} rows)</p>}
            {fullFile && <p>Full List CSV: <span className="font-medium">{fullFile.name}</span> ({fullRowCount.toLocaleString()} rows)</p>}
          </div>
        </div>

        {/* Classification (for bad cache) */}
        {needsClassification && (
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Bad Number Classification</h3>
            <p className="text-sm text-gray-600">
              {CLASSIFICATION_OPTIONS.find(o => o.value === classification)?.label} / {SUB_TYPE_OPTIONS[classification]?.find(o => o.value === subType)?.label}
            </p>
          </div>
        )}

        {/* Column mappings summary */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Column Mappings</h3>
          <div className="space-y-2 text-sm text-gray-600">
            {needsCleanFile && (
              <div>
                <span className="font-medium">Clean file: </span>
                {Object.entries(cleanMapping).map(([col, field]) => (
                  <span key={col} className="inline-block bg-white border border-gray-200 rounded px-2 py-0.5 mr-1 text-xs">
                    {cleanHeaders[parseInt(col)]} → {MAPPABLE_FIELDS.find(f => f.value === field)?.label}
                  </span>
                ))}
              </div>
            )}
            {needsBadFile && (
              <div>
                <span className="font-medium">Bad file: </span>
                {Object.entries(badMapping).map(([col, field]) => (
                  <span key={col} className="inline-block bg-white border border-gray-200 rounded px-2 py-0.5 mr-1 text-xs">
                    {badHeaders[parseInt(col)]} → {MAPPABLE_FIELDS.find(f => f.value === field)?.label}
                  </span>
                ))}
              </div>
            )}
            {needsFullFile && (
              <div>
                <span className="font-medium">Full list file: </span>
                {Object.entries(fullMapping).map(([col, field]) => (
                  <span key={col} className="inline-block bg-white border border-gray-200 rounded px-2 py-0.5 mr-1 text-xs">
                    {fullHeaders[parseInt(col)]} → {MAPPABLE_FIELDS.find(f => f.value === field)?.label}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notices */}
      <div className="space-y-3">
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Existing cache entries with the same phone number will be updated with the imported data.</span>
        </div>

        {(mode === 'good' || mode === 'both') && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>All numbers added to the good cache are expected to have been through both a landline and TCPA scrub prior to import.</span>
          </div>
        )}

        {(mode === 'bad_diff' || (mode === 'both' && badSubMode === 'diff')) && (
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>Bad numbers will be derived by finding phone numbers in the full list that are NOT present in the clean list.</span>
          </div>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );

  const renderResultsStep = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <CheckCircle className="w-8 h-8 text-green-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Import Complete</h2>
          <p className="text-sm text-gray-500">Your cache import has finished successfully.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {results?.goodCache && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-5 space-y-3">
            <h3 className="font-semibold text-green-800 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Good Cache Results
            </h3>
            <div className="space-y-1 text-sm">
              <p className="text-green-700">Rows processed: <span className="font-semibold">{results.goodCache.processed.toLocaleString()}</span></p>
              <p className="text-green-700">Imported/Updated: <span className="font-semibold">{results.goodCache.imported.toLocaleString()}</span></p>
              {results.goodCache.invalidPhones > 0 && (
                <p className="text-amber-600">Invalid phones skipped: <span className="font-semibold">{results.goodCache.invalidPhones.toLocaleString()}</span></p>
              )}
              {results.goodCache.duplicatesInFile > 0 && (
                <p className="text-gray-600">Duplicates in file: <span className="font-semibold">{results.goodCache.duplicatesInFile.toLocaleString()}</span></p>
              )}
            </div>
          </div>
        )}

        {results?.badCache && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 space-y-3">
            <h3 className="font-semibold text-red-800 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Bad Cache Results
            </h3>
            <div className="space-y-1 text-sm">
              {results.badCache.fromDiff && (
                <p className="text-red-700">Bad numbers found via diff: <span className="font-semibold">{results.badCache.diffBadFound?.toLocaleString()}</span></p>
              )}
              <p className="text-red-700">Rows processed: <span className="font-semibold">{results.badCache.processed.toLocaleString()}</span></p>
              <p className="text-red-700">Imported/Updated: <span className="font-semibold">{results.badCache.imported.toLocaleString()}</span></p>
              {results.badCache.invalidPhones > 0 && (
                <p className="text-amber-600">Invalid phones skipped: <span className="font-semibold">{results.badCache.invalidPhones.toLocaleString()}</span></p>
              )}
              {results.badCache.duplicatesInFile > 0 && (
                <p className="text-gray-600">Duplicates in file: <span className="font-semibold">{results.badCache.duplicatesInFile.toLocaleString()}</span></p>
              )}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={resetWizard}
        className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
      >
        Start New Import
      </button>
    </div>
  );

  // === Main render ===

  const stepIndex = ['mode', 'upload', 'mapping', 'review', 'results'].indexOf(step);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Cache Import</h1>
          <p className="text-sm text-gray-500 mt-1">Bulk import phone numbers into the good and/or bad phone cache via CSV.</p>
        </div>
      </div>

      {/* Progress steps */}
      {step !== 'results' && (
        <div className="flex items-center gap-2 text-sm">
          {['Mode', 'Upload', 'Mapping', 'Review'].map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              {i > 0 && <div className={`w-8 h-px ${i <= stepIndex ? 'bg-blue-400' : 'bg-gray-300'}`} />}
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                i === stepIndex ? 'bg-blue-600 text-white' :
                i < stepIndex ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-500'
              }`}>
                {label}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Card wrapper */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {step === 'mode' && renderModeStep()}
        {step === 'upload' && renderUploadStep()}
        {step === 'mapping' && renderMappingStep()}
        {step === 'review' && renderReviewStep()}
        {step === 'results' && renderResultsStep()}
      </div>

      {/* Navigation buttons */}
      {step !== 'mode' && step !== 'results' && (
        <div className="flex justify-between">
          <button
            onClick={() => {
              if (step === 'upload') setStep('mode');
              else if (step === 'mapping') setStep('upload');
              else if (step === 'review') setStep('mapping');
            }}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {step === 'upload' && (
            <button
              onClick={() => setStep('mapping')}
              disabled={!canProceedFromUpload()}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              Next: Column Mapping
              <ArrowRight className="w-4 h-4" />
            </button>
          )}

          {step === 'mapping' && (
            <button
              onClick={() => setStep('review')}
              disabled={!canProceedFromMapping()}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              Next: Review
              <ArrowRight className="w-4 h-4" />
            </button>
          )}

          {step === 'review' && (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Import
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
